import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Tabs, Icon, Button, Badge, ArchImage } from '../../components/ui';
import { GUIDE_TABS, GUIDE, DID_YOU_KNOW } from '../../data/travelGuide';

const VALID = new Set(GUIDE_TABS.map((t) => t.id));

export default function TravelGuide() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const fromHash = location.hash.replace('#', '');
  const [tab, setTab] = useState(VALID.has(fromHash) ? fromHash : 'reach');

  useEffect(() => {
    if (VALID.has(fromHash)) setTab(fromHash);
  }, [fromHash]);
  const change = (id) => { setTab(id); navigate({ pathname: location.pathname, hash: id }, { replace: true }); };

  const g = GUIDE[tab];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'TravelAction',
        name: 'Plan a pilgrimage to Ayodhya',
        toLocation: {
          '@type': 'City',
          name: 'Ayodhya',
          address: { '@type': 'PostalAddress', addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' },
        },
      },
      ...GUIDE.itineraries.items.map((it) => ({
        '@type': 'TouristTrip',
        name: it.title,
        description: `A ${it.days}-day itinerary through Ayodhya.`,
        url: 'https://ayodhyadham.info/travel-guide#itineraries',
        itinerary: {
          '@type': 'ItemList',
          numberOfItems: it.stops.length,
          itemListElement: it.stops.map((stop, i) => ({
            '@type': 'ListItem', position: i + 1, name: stop,
          })),
        },
      })),
      {
        '@type': 'FAQPage',
        mainEntity: GUIDE.reach.items.map((item) => ({
          '@type': 'Question',
          name: `${item.title} — how do I reach Ayodhya?`,
          acceptedAnswer: { '@type': 'Answer', text: item.body },
        })),
      },
    ],
  };

  return (
    <>
      <SEO
        title="Ayodhya Travel Guide"
        description="Everything you need to plan a trip to Ayodhya: flights, trains and roads, where to stay, local transport, the best season, travel tips and 1–3 day itineraries."
        canonicalUrl="/travel-guide"
        keywords="Ayodhya travel guide, how to reach Ayodhya, best time to visit Ayodhya, Ayodhya itinerary, where to stay in Ayodhya"
        structuredData={schema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Travel Guide') }]} title={t('Travel Guide')} subtitle={t('Plan your perfect trip to Ayodhya')} />

      <div className="container content-wrap">
        <Tabs variant="pill" items={GUIDE_TABS.map((x) => ({ ...x, label: t(x.label) }))} value={tab} onChange={change} ariaLabel={t('Travel guide sections')} />

        <div className="content-cols" style={{ marginTop: 28 }}>
          <div className="guide__panel" key={tab} id={tab}>
            <h2 className="h-section">{t(g.title)}</h2>
            <p className="guide__intro">{t(g.intro)}</p>

            {(tab === 'reach' || tab === 'stay' || tab === 'transport') && (
              <div className={tab === 'stay' ? 'grid-2' : 'grid-3'}>
                {g.items.map((it) => (
                  <div key={it.title} className="card guide-card">
                    <span className="guide-card__icon"><Icon name={it.icon} size={22} /></span>
                    <div className="guide-card__title">{t(it.title)}</div>
                    <p className="guide-card__body">{t(it.body)}</p>
                    {it.to && <Button size="sm" variant="outline" to={it.to} icon={<Icon name="arrow-right" size={14} />}>{t(it.cta || 'Explore')}</Button>}
                  </div>
                ))}
              </div>
            )}

            {tab === 'when' && (
              <div className="grid-3">
                {g.seasons.map((s) => (
                  <div key={s.label} className={`card season${s.best ? ' is-best' : ''}`}>
                    <div className="season__head">
                      <span className="season__label">{s.label}</span>
                      {s.best ? <Badge variant="gold">{t('Best time')}</Badge> : <Icon name={s.icon} size={20} className="season__icon" />}
                    </div>
                    <p className="guide-card__body">{t(s.body)}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === 'tips' && (
              <ul className="tips-grid">
                {g.items.map((tip) => (
                  <li key={tip}><Icon name="circle-check" size={18} />{t(tip)}</li>
                ))}
              </ul>
            )}

            {tab === 'itineraries' && (
              <>
                <div className="grid-3">
                  {g.items.map((it) => (
                    <div key={it.days} className="card itin">
                      <div className="itin__head">
                        <span className="itin__days">{it.days}<small>{it.days === 1 ? t('day') : t('days')}</small></span>
                        <span className="itin__title">{t(it.title)}</span>
                      </div>
                      <ol className="itin__stops">
                        {it.stops.map((s) => <li key={s}>{t(s)}</li>)}
                      </ol>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 22 }}>
                  <Button to="/travel-planner" icon={<Icon name="route" size={16} />} iconPosition="left">{t('Build a custom itinerary')}</Button>
                </div>
              </>
            )}
          </div>

          <aside className="content-cols__aside">
            <ArchImage src="/images/hanuman/temple-1.webp" alt={t('Hanuman Garhi, Ayodhya')} ratio="4/3" />
            <div className="card aside-note">
              <div className="aside-note__title">{t('Did you know?')}</div>
              <ul>{DID_YOU_KNOW.map((d) => <li key={d}>{t(d)}</li>)}</ul>
            </div>
            <div className="card guide-card">
              <span className="guide-card__icon"><Icon name="temple" size={22} /></span>
              <div className="guide-card__title">{t('Ready for darshan?')}</div>
              <p className="guide-card__body">{t('Check timings and plan your Ram Mandir visit in a few steps.')}</p>
              <Button size="sm" to="/darshan-booking" icon={<Icon name="arrow-right" size={14} />}>{t('Darshan Booking')}</Button>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
